import React, { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Search, X, CheckCircle2 } from "lucide-react";

export default function SchoolAutocomplete({ value, onChange, testId = "school-autocomplete" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(value || null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (selected) {
      setQuery(selected.name);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/schools", { params: { q: query, limit: 8 } });
        setResults(data);
      } catch (e) {
        console.error(formatApiError(e));
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, selected]);

  const pick = (s) => {
    setSelected(s);
    setQuery(s.name);
    setOpen(false);
    onChange(s);
  };

  const clear = () => {
    setSelected(null);
    setQuery("");
    onChange(null);
    setOpen(true);
  };

  return (
    <div className="relative" data-testid={testId}>
      <div className="flex items-center bg-[#0A0A0A] border border-white/10 rounded-md focus-within:border-cyan8r/60 transition">
        <Search size={16} className="ml-3 text-zinc-500" />
        <input
          data-testid={`${testId}-input`}
          type="text"
          autoComplete="off"
          value={query}
          onChange={(e) => {
            setSelected(null);
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Start typing your school..."
          className="flex-1 bg-transparent px-3 py-3 text-white placeholder-zinc-500 outline-none text-sm"
        />
        {selected && (
          <button type="button" onClick={clear} className="mr-3 text-zinc-500 hover:text-white">
            <X size={14} />
          </button>
        )}
      </div>
      {open && !selected && results.length > 0 && (
        <div className="absolute z-30 mt-2 w-full bg-[#0F0F0F] border border-white/10 rounded-md shadow-2xl max-h-72 overflow-auto">
          {results.map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={`school-option-${s.id}`}
              onClick={() => pick(s)}
              className="w-full text-left px-4 py-3 hover:bg-white/5 border-b border-white/5 last:border-0 transition"
            >
              <div className="text-sm text-white">{s.name}</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {s.city ? `${s.city}, ` : ""}{s.state} · {s.accreditation_type}
              </div>
            </button>
          ))}
        </div>
      )}
      {selected && (
        <div className="mt-2 flex items-center gap-2 text-xs text-cyan8r">
          <CheckCircle2 size={14} /> Selected: {selected.name}
        </div>
      )}
    </div>
  );
}
